import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { DiscoveryRulesForm } from '@/settings/discovery-rules/form'
import type { DiscoveryRulesValue } from '@/settings/discovery-rules/form'
import { summariseDiscoveryRules } from '@/settings/discovery-rules/summary'
import { may, useActiveTenant } from '@/tenant/context'
import { SettingRow } from '@/ui/SettingRow'
import { useOneEditing } from '@/ui/useOneEditing'
import { ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Settings › Outreach › Discovery rules.
 *
 * Was `FilterCard` on the Niche packs screen until the settings consolidation
 * of 2026-09-15. Reads the same `GET /outreach/keywords` the packs screen
 * reads and writes `PUT /outreach/discovery-settings`, gated on
 * `discovery:manage` as that endpoint is.
 */
export function DiscoveryRulesPanel(): JSX.Element {
  const brand = useBrand()
  const { membership } = useActiveTenant()
  const editable = may(membership, 'discovery:manage')
  const toast = useToast()
  const one = useOneEditing()
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const discovery = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/keywords', { signal })),
    [],
  )
  const save = useMutation((body: DiscoveryRulesValue) =>
    call(() => api.PUT('/api/v1/outreach/discovery-settings', { body })),
  )

  if (discovery.state.status === 'loading') {
    return <Loading label="Loading your discovery rules" lines={2} />
  }
  if (discovery.state.status === 'error') {
    return (
      <ErrorState
        failure={discovery.state.failure}
        title="Could not load your discovery rules"
        onRetry={discovery.refetch}
      />
    )
  }
  const filter = discovery.state.data.settings

  return (
    <div className="ar-stack">
      <h2 className="ar-h2">Discovery rules</h2>
      <SettingRow
        label="What counts as a lead"
        summary={summariseDiscoveryRules(filter)}
        savedAt={savedAt}
        editable={editable}
        editing={one.editing === 'rules'}
        onEdit={() => {
          one.open('rules')
        }}
        onCancel={one.close}
      >
        <DiscoveryRulesForm
          value={{ max_age_hours: filter.max_age_hours, exclude_phrases: filter.exclude_phrases }}
          customised={filter.customised}
          productName={brand.productName}
          submitLabel="Save"
          onSubmit={async (next) => {
            const result = await save.run(next)
            if (result.ok) {
              setSavedAt(new Date().toISOString())
              one.close()
              discovery.refetch()
              toast.show({ kind: 'success', title: 'Saved', body: 'The next search uses these.' })
            }
            return result
          }}
        />
      </SettingRow>
    </div>
  )
}
